import { Accordion, CarrierLogo, useAccordion } from '@eti/components';
import { Card, Inline, Stack, Text, vars } from '@etg/wings';
import { css, cx } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import type { GroupedAirlineReferenceInfoType } from '../../utils';
import RouteInfo from './RouteInfo';
import AirlineReferenceInfo from './AirlineReferenceInfo';

const borderStyles = css`
  border: 1px solid ${vars.colors.divider};
`;

const carrierLogoStyles = css`
  width: 1.875rem;
`;

const dividerStyles = css`
  border-bottom: 1px solid ${vars.colors.divider};
`;

interface AirlineReferenceInfoWrapperProps {
  airlineReferenceInfo: GroupedAirlineReferenceInfoType;
  dataTestId: string;
}

const AirlineReferenceInfoWrapper = ({
  airlineReferenceInfo,
  dataTestId,
}: AirlineReferenceInfoWrapperProps) => {
  const { t } = useTranslation();
  const { isAnimationCompleted } = useAccordion();

  return (
    <AirlineReferenceInfo
      airlineReferenceInfo={airlineReferenceInfo}
      dataTestId={`${dataTestId}-airline-reference-accordion`}
      descriptionContent={<Text asHtml>{t('CheckIn.NotPurchased.Guidelines.Description')}</Text>}
      isContentReady={isAnimationCompleted}
    />
  );
};

interface AirlineReferenceAccordionProps {
  airlineReferenceInfo: GroupedAirlineReferenceInfoType;
  dataTestId: string;
  isInitiallyExpanded?: boolean;
  shouldAddBorder?: boolean;
}

const AirlineReferenceAccordion = ({
  airlineReferenceInfo,
  dataTestId,
  isInitiallyExpanded = false,
  shouldAddBorder = false,
}: AirlineReferenceAccordionProps) => {
  return (
    <Card
      className={cx({ [borderStyles]: shouldAddBorder })}
      data-testid={`${dataTestId}-airline-reference-accordion-wrapper`}
    >
      <Accordion
        id="airline-reference-accordion"
        isInitiallyExpanded={isInitiallyExpanded}
        spacing={16}
        variant="full-width"
      >
        <Accordion.Header
          alignY="top"
          data-testid={`${dataTestId}-airline-reference-accordion-header`}
        >
          <Inline alignY="center" noWrap>
            {airlineReferenceInfo.carrierBookingNumber?.carrierCode &&
              airlineReferenceInfo.carrierBookingNumber.carrierName && (
                <CarrierLogo
                  className={carrierLogoStyles}
                  data-testid={`${dataTestId}-airline-reference-accordion-carrier-logo`}
                  id={airlineReferenceInfo.carrierBookingNumber.carrierCode}
                  imageAlt={airlineReferenceInfo.carrierBookingNumber.carrierName}
                />
              )}
            <RouteInfo
              dataTestId={`${dataTestId}-airline-reference-accordion`}
              route={{
                destination: {
                  cityName: airlineReferenceInfo.destination.cityName,
                  cityCode: airlineReferenceInfo.destination.cityCode,
                },
                origin: {
                  cityName: airlineReferenceInfo.origin.cityName,
                  cityCode: airlineReferenceInfo.origin.cityCode,
                },
                arrivedAt: airlineReferenceInfo.arrivedAt,
                departuredAt: airlineReferenceInfo.departuredAt,
              }}
            />
          </Inline>
        </Accordion.Header>
        <Accordion.Body>
          <Stack>
            <div className={dividerStyles} />
            <AirlineReferenceInfoWrapper
              airlineReferenceInfo={airlineReferenceInfo}
              dataTestId={dataTestId}
            />
          </Stack>
        </Accordion.Body>
      </Accordion>
    </Card>
  );
};

export default AirlineReferenceAccordion;
